/* eslint-disable max-classes-per-file */

import _ from 'underscore';

import { Registry } from './registry';
import {
  defineConstProperty,
  defineProducedPropertyOnClass,
} from './reflection';
import * as url from './url-util';
import type {
  CallNavigationOptions,
  CallableNavigationOptions,
  CastNavigationOptions,
  EdmCallableType,
  EdmCollectionType,
  EdmObjectType,
  EdmProperty,
  EdmType,
  EntityDataModel,
  FunctionNavigationJson,
  Navigation as NavigationContract,
  NavigationJson,
  NavigationOptions,
  ODataValue,
  PropertyNavigationJson,
  PropertyNavigationOptions,
  ResourceIdentifier as ResourceIdentifierContract,
  ResourceIdentifierConstructor,
  ResourceIdentifierOptions,
  ResourceIdentifierRegistry,
  ResourceKey,
  TypeName,
  TypeRegistry,
  WithKeyNavigationOptions,
} from '../types';

interface TypedResourceIdentifier extends ResourceIdentifierContract {
  readonly type: EdmType;
}

/**
 * @param {EDM} edm - The EDM object to apply this plugin to
 * @return {Void} nothing to return
 */
export function resIdsPlugin(edm: EntityDataModel) {
  if (edm.resourceIdentifiers) {
    return;
  }

  const types = edm.types as TypeRegistry;

  defineConstProperty(edm, 'resourceIdentifiers', (() => {
    const resourceIdentifiers = new Registry<ResourceIdentifierConstructor>();

    class Navigation {
      constructor({ source }: NavigationOptions) {
        defineConstProperty(this, 'source', source);
      }

      toJSON() {
        return [...this.source.toJSON(), this.toSelfJSON()];
      }

      // eslint-disable-next-line class-methods-use-this
      toSelfJSON(): NavigationJson {
        throw new Error('I\'m abstract');
      }
    }
    interface Navigation extends NavigationContract {}

    class PropertyNavigation extends Navigation {
      constructor({
        source,
        property,
        name,
      }: PropertyNavigationOptions) {
        super({
          source,
        });
        defineConstProperty(this, 'property', property);
        defineConstProperty(this, 'name', name);
        defineConstProperty(this, 'path', url.join(source.path, name));
      }

      toSelfJSON(): PropertyNavigationJson {
        return {
          type: 'property',
          name: this.name,
        };
      }

      static defineOn(
        TypeResID: ResourceIdentifierConstructor,
        navigationProperties: Record<string, EdmProperty>,
      ) {
        _.each(navigationProperties, (property, name) => {
          /**
           * Navigation property factory for the defined property
           * @memberof TypeResID#
           * @this TypeResID
           * @returns {ResourceIdentifier} The ResourceIdentifier to access the property
           */
          function navPropFactory(this: ResourceIdentifierContract) {
            const propertyType = property.type as EdmType;
            return new propertyType.ResourceIdentifier({
              navigation: new PropertyNavigation({ source: this, property, name }),
            });
          }

          defineProducedPropertyOnClass(TypeResID, name, navPropFactory);
        });
      }
    }
    interface PropertyNavigation extends NavigationContract {
      readonly property: EdmProperty;
      readonly name: string;
    }

    class CallableNavigation extends Navigation {
      constructor({
        source,
        name,
      }: CallableNavigationOptions) {
        super({
          source,
        });
        defineConstProperty(this, 'name', name);
        defineConstProperty(this, 'path', url.join(source.path, name));
      }

      static defineOn(
        TypeResID: ResourceIdentifierConstructor,
        callable: {
          actions: EdmCallableType[];
          functions: EdmCallableType[];
        },
      ) {
        function defineCallableOn(
          resID: ResourceIdentifierConstructor,
          callableTypes: EdmCallableType[],
        ) {
          _.each(callableTypes, type => {
            /**
             * factory for the callables
             * @memberof resID#
             * @this resID
             * @returns {ResourceIdentifier} The ResourceIdentifier to access the property
             */
            function factory(this: ResourceIdentifierContract) {
              return new type.ResourceIdentifier({
                navigation: new CallableNavigation({
                  source: this,
                  name: type.callableName,
                }),
              });
            }

            defineProducedPropertyOnClass(resID, type.callableName, factory);
          });
        }
        defineCallableOn(TypeResID, callable.actions);
        defineCallableOn(TypeResID, callable.functions);
      }
    }
    interface CallableNavigation extends NavigationContract {
      readonly name: string;
    }

    class CastNavigation extends Navigation {
      constructor({
        source,
        type,
        name = type.name,
      }: CastNavigationOptions) {
        super({
          source,
        });

        defineConstProperty(this, 'type', type);
        defineConstProperty(this, 'name', name);
        defineConstProperty(this, 'path', url.join(source.path, name));
      }

      toSelfJSON(): FunctionNavigationJson {
        return {
          type: 'function',
          name: CastNavigation.navigationName,
          parameters: [this.name],
        };
      }

      static get navigationName() {
        return '$cast';
      }

      static defineOn(TypeResID: ResourceIdentifierConstructor) {
        /**
         * $cast navigation method of the TypeResID
         * @memberof TypeResID#
         * @this TypeResID
         * @param {String} name - The name of the subclass
         * @returns {TypeResID} The ResourceIdentifier to access the subclass object
         */
        function navCastMethod(this: TypedResourceIdentifier, name: TypeName) {
          const entityType = types.resolve(name, this.type.namespace) as EdmObjectType;
          const type = TypeResID.prototype.type instanceof types.CollectionType
            ? entityType.collectionType
            : entityType;
          const navigation = new CastNavigation({ source: this, type: entityType, name });

          return new type.ResourceIdentifier({ navigation });
        }

        defineConstProperty(TypeResID.prototype, CastNavigation.navigationName, navCastMethod);
      }
    }
    interface CastNavigation extends NavigationContract {
      readonly type: EdmType;
      readonly name: TypeName;
    }

    class WithKeyNavigation extends Navigation {
      constructor({
        source,
        key,
      }: WithKeyNavigationOptions) {
        super({
          source,
        });

        defineConstProperty(this, 'key', key);
        defineConstProperty(this, 'path', (() => {
          // In case people pass a decimal string for a integer key
          // We cannot use parseInt directly, it would get a wrong number if the
          // key is beyond Number.MAX_SAFE_INTEGER
          if (_.isString(key)) {
            const keyType = _.chain(source)
              .result('type')
              .result('elementType')
              .result('keyProperty')
              .result('typeName')
              .value() as unknown as TypeName | null;

            if (keyType === 'integer') {
              if (key.match(/^[+-]?(0|[1-9][0-9]*)$/)) {
                return `${source.path}(${key})`;
              }
            }
            if (keyType === 'string') {
              return `${source.path}('${key}')`;
            }
          }

          return `${source.path}(${JSON.stringify(key)})`;
        })());
      }

      toSelfJSON(): FunctionNavigationJson {
        return {
          type: 'function',
          name: WithKeyNavigation.navigationName,
          parameters: [this.key],
        };
      }

      static get navigationName() {
        return '$withKey';
      }

      static defineOn(TypeResID: ResourceIdentifierConstructor) {
        /**
         * $withKey navigation method of the TypeResID
         * @memberof TypeResID#
         * @this TypeResID
         * @param {String} key - The key of the element
         * @returns {ResourceIdentifier}
         * The ResourceIdentifier to access the element with the given key
         */
        function navWithKeyMethod(
          this: TypedResourceIdentifier & { type: EdmCollectionType },
          key: ResourceKey,
        ) {
          const navigation = new WithKeyNavigation({ source: this, key });

          return new this.type.elementType.ResourceIdentifier({ navigation });
        }

        defineConstProperty(
          TypeResID.prototype,
          WithKeyNavigation.navigationName,
          navWithKeyMethod,
        );
      }
    }
    interface WithKeyNavigation extends NavigationContract {
      readonly key: ResourceKey;
    }

    class CallNavigation extends Navigation {
      constructor({
        source,
        parameters = {},
      }: CallNavigationOptions) {
        super({
          source,
        });
        defineConstProperty(this, 'parameters', parameters);

        const path = source.type instanceof types.ActionType
          ? source.path
          : `${source.path}(${_.map(
            parameters,
            (value, name) => `${name}=${value}`,
          ).join(',')})`;

        defineConstProperty(this, 'path', path);
      }

      toSelfJSON(): FunctionNavigationJson {
        return {
          type: 'function',
          name: CallNavigation.navigationName,
          // for callable, parameters are named
          parameters: [this.parameters],
        };
      }

      static get navigationName() {
        return '$call';
      }

      static defineOn(TypeResID: ResourceIdentifierConstructor) {
        /**
         * $call navigation method of the TypeResID
         * @memberof TypeResID#
         * @this TypeResID
         * @param {object} parameters - The parameters to call the callable
         * @return {ResourceIdentifier}
         * The ResourceIdentifier accessing the return value of the callable
         */
        function navCallMethod(
          this: TypedResourceIdentifier & { type: EdmCallableType },
          parameters: Record<string, ODataValue>,
        ) {
          const navigation = new CallNavigation({ source: this, parameters });
          const returnType = this.type.returnType as EdmType;

          return new returnType.ResourceIdentifier({ navigation });
        }

        defineConstProperty(TypeResID.prototype, CallNavigation.navigationName, navCallMethod);
      }
    }
    interface CallNavigation extends NavigationContract {
      readonly parameters: Record<string, ODataValue>;
    }

    class ResourceIdentifier {
      constructor({
        navigation,
      }: ResourceIdentifierOptions = {}) {
        if (navigation) {
          defineConstProperty(this, 'navigation', navigation);
          defineConstProperty(this, 'path', navigation.path);
        } else {
          defineConstProperty(this, 'path', '');
        }
      }

      toJSON() {
        if (this.navigation) {
          return this.navigation.toJSON();
        }

        return [];
      }

      identifyEntitySet(json: NavigationJson[]) {
        // eslint-disable-next-line
        let entitySet: ResourceIdentifierContract = this;

        /* eslint guard-for-in: 0 */
        /* eslint no-restricted-syntax: 0 */
        for (const i in json) {
          const item = json[i];

          if (item.type === 'property') {
            entitySet = entitySet[item.name] as ResourceIdentifierContract;
          } else if (item.type === 'function') {
            const navigationMethod = entitySet[item.name] as (
              ...parameters: ODataValue[]
            ) => ResourceIdentifierContract;
            entitySet = navigationMethod.apply(entitySet, item.parameters);
          }

          if (!entitySet) {
            return null;
          }
        }

        return entitySet;
      }
    }
    interface ResourceIdentifier extends ResourceIdentifierContract {}

    class CollectionResourceIdentifier extends ResourceIdentifier {
      constructor({
        navigation,
      }: ResourceIdentifierOptions = {}) {
        super({ navigation });
      }
    }

    function resourceIdentifierForType(type: EdmType) {
      // duck type, if the type has a "baseType", use its ResourceIdentifier as the super class
      const DefaultBase = type instanceof types.CollectionType
        ? CollectionResourceIdentifier
        : ResourceIdentifier;
      const { baseType } = type as EdmObjectType;
      const Base = baseType ? baseType.ResourceIdentifier : DefaultBase;

      const AccessorType = class extends Base {
      };

      defineConstProperty(AccessorType.prototype, 'type', type);

      resourceIdentifiers.register(AccessorType, type.name);

      return AccessorType;
    }

    const resourceIdentifierFactories: Record<
      'PrimitiveType' | 'ObjectType' | 'CollectionType' | 'CallableType',
      { factory(this: EdmType): ResourceIdentifierConstructor }
    > = {
      PrimitiveType: {
        factory() {
          return resourceIdentifierForType(this);
        },
      },

      ObjectType: {
        factory() {
          const ObjectTypeResID = resourceIdentifierForType(this);
          const objectType = this as EdmObjectType;

          CastNavigation.defineOn(ObjectTypeResID);
          PropertyNavigation.defineOn(ObjectTypeResID, objectType.navigationProperties);
          if (objectType.callable) {
            CallableNavigation.defineOn(ObjectTypeResID, objectType.callable);
          }
          return ObjectTypeResID;
        },
      },

      CollectionType: {
        factory() {
          const CollectionTypeResID = resourceIdentifierForType(this);
          const collectionType = this as EdmCollectionType;

          CastNavigation.defineOn(CollectionTypeResID);
          // Only the entity sets can be navigated with keys
          if (collectionType.elementType instanceof types.EntityType) {
            WithKeyNavigation.defineOn(CollectionTypeResID);
          }
          PropertyNavigation.defineOn(
            CollectionTypeResID,
            collectionType.navigationProperties,
          );
          if (collectionType.callable) {
            CallableNavigation.defineOn(CollectionTypeResID, collectionType.callable);
          }

          return CollectionTypeResID;
        },
      },

      CallableType: {
        factory() {
          const CallableTypeResID = resourceIdentifierForType(this);

          CallNavigation.defineOn(CallableTypeResID);

          return CallableTypeResID;
        },
      },
    };

    _.each(resourceIdentifierFactories, (def, typeName) => {
      const typeConstructors: Pick<
        TypeRegistry,
        'PrimitiveType' | 'ObjectType' | 'CollectionType' | 'CallableType'
      > = types;
      defineProducedPropertyOnClass(
        typeConstructors[typeName as keyof typeof typeConstructors],
        'ResourceIdentifier',
        def.factory,
      );
    });

    defineConstProperty(resourceIdentifiers, 'ResourceIdentifier', ResourceIdentifier);
    defineConstProperty(
      resourceIdentifiers,
      'CollectionResourceIdentifier',
      CollectionResourceIdentifier,
    );
    defineConstProperty(resourceIdentifiers, 'Navigation', Navigation);
    defineConstProperty(resourceIdentifiers, 'PropertyNavigation', PropertyNavigation);
    defineConstProperty(resourceIdentifiers, 'CastNavigation', CastNavigation);
    defineConstProperty(resourceIdentifiers, 'WithKeyNavigation', WithKeyNavigation);
    defineConstProperty(resourceIdentifiers, 'CallNavigation', CallNavigation);
    defineConstProperty(resourceIdentifiers, 'CallableNavigation', CallableNavigation);

    return resourceIdentifiers as unknown as ResourceIdentifierRegistry;
  })());
}
